import { NavLink, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { apiRemote } from "@/lib/apiRemote";
import {
  House,
  MagnifyingGlass,
  PlusCircle,
  Heart,
  ChatCircle,
  UserCircle,
  SignOut,
  Buildings,
  ShieldCheck,
  Rocket,
  ChartLineUp,
  ChartBar,
  DownloadSimple,
  BellRinging,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

const nav = [
  { to: "/", label: "Accueil", icon: House, testid: "nav-home" },
  { to: "/rechercher", label: "Rechercher", icon: MagnifyingGlass, testid: "nav-search" },
  { to: "/publier", label: "Publier", icon: PlusCircle, testid: "nav-publish", primary: true },
  { to: "/favoris", label: "Favoris", icon: Heart, testid: "nav-favorites" },
  { to: "/messages", label: "Messages", icon: ChatCircle, testid: "nav-messages", badgeKey: "unread" },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!user) {
      setUnread(0);
      return;
    }
    const fetchUnread = () => {
      // Source of truth = remote mobile backend /notifications (unread)
      apiRemote
        .get("/notifications", { validateStatus: () => true })
        .then((r) => {
          if (r.status >= 200 && r.status < 300) {
            const items = r.data?.notifications || [];
            setUnread(items.filter((n) => !n.read).length);
          }
        })
        .catch(() => {});
    };
    fetchUnread();
    const id = setInterval(() => !document.hidden && fetchUnread(), 30000);
    const onExternal = () => fetchUnread();
    window.addEventListener("refresh-unread", onExternal);
    return () => {
      clearInterval(id);
      window.removeEventListener("refresh-unread", onExternal);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  const badges = { unread };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Top navbar */}
      <header className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 md:px-8">
          <Link to="/" className="flex items-center gap-2" data-testid="app-logo">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground">
              <Buildings size={22} weight="duotone" />
            </div>
            <div className="flex flex-col leading-tight">
              <span className="font-display text-lg font-black tracking-tight">BODJE IMMO</span>
              <span className="text-[10px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
                Guinée · Immobilier
              </span>
            </div>
          </Link>

          <nav className="hidden items-center gap-1 md:flex">
            {nav.map((item) => {
              const badge = item.badgeKey ? badges[item.badgeKey] : 0;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={item.testid + "-desktop"}
                  className={({ isActive }) =>
                    `btn-pill relative flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold ${
                      isActive
                        ? "bg-secondary text-secondary-foreground"
                        : "text-foreground/70 hover:bg-accent hover:text-foreground"
                    }`
                  }
                >
                  <item.icon size={18} weight="duotone" />
                  {item.label}
                  {badge > 0 && (
                    <span
                      data-testid={`badge-${item.testid}-desktop`}
                      className="ml-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1.5 text-[10px] font-bold text-primary-foreground"
                    >
                      {badge > 99 ? "99+" : badge}
                    </span>
                  )}
                </NavLink>
              );
            })}
          </nav>

          <div className="flex items-center gap-2">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="user-menu-trigger"
                    className="btn-pill flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-sm font-semibold hover:bg-accent"
                  >
                    {user.picture ? (
                      <img src={user.picture} alt="" className="h-7 w-7 rounded-full object-cover" />
                    ) : (
                      <div className="flex h-7 w-7 items-center justify-center rounded-full bg-secondary text-secondary-foreground text-xs font-bold">
                        {(user.name || user.email || "?").charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span className="hidden max-w-[120px] truncate md:inline">{user.name || user.email}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="truncate">{user.email}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/bureau")} data-testid="menu-bureau">
                    <ChartBar className="mr-2" size={18} /> Bureau Agent
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/profil")} data-testid="menu-profile">
                    <UserCircle className="mr-2" size={18} /> Mon profil
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/mes-annonces")} data-testid="menu-my-listings">
                    <Buildings className="mr-2" size={18} /> Mes annonces
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/favoris")} data-testid="menu-favorites">
                    <Heart className="mr-2" size={18} /> Favoris
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/alertes")} data-testid="menu-alerts">
                    <BellRinging className="mr-2" size={18} /> Mes alertes
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/tarifs")} data-testid="menu-pricing">
                    <Rocket className="mr-2" size={18} /> Booster / Agence
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => window.dispatchEvent(new CustomEvent("bodje-open-install"))}
                    data-testid="menu-install"
                  >
                    <DownloadSimple className="mr-2" size={18} /> Installer l'app
                  </DropdownMenuItem>
                  {user.role === "admin" && (
                    <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                      <ShieldCheck className="mr-2" size={18} /> Console admin
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                    <SignOut className="mr-2" size={18} /> Déconnexion
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button
                  variant="ghost"
                  onClick={() => navigate("/login")}
                  data-testid="header-login-btn"
                  className="rounded-full font-semibold"
                >
                  Connexion
                </Button>
                <Button
                  onClick={() => navigate("/register")}
                  data-testid="header-register-btn"
                  className="btn-pill rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90"
                >
                  S'inscrire
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="pb-24 md:pb-0">{children}</main>

      {/* Footer */}
      <footer className="hidden border-t border-border bg-card md:block">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-4 px-8 py-8 md:flex-row md:items-center">
          <div>
            <div className="font-display text-xl font-black">BODJE IMMO</div>
            <div className="text-sm text-muted-foreground">
              La plateforme immobilière de référence en Guinée.
            </div>
          </div>
          <div className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} BODJE IMMO · Conakry, République de Guinée
          </div>
        </div>
      </footer>

      {/* Mobile bottom nav */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-background/95 backdrop-blur-md md:hidden"
        data-testid="mobile-bottom-nav"
      >
        <div className="mx-auto grid max-w-lg grid-cols-5">
          {nav.map((item) => {
            const badge = item.badgeKey ? badges[item.badgeKey] : 0;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `relative flex flex-col items-center justify-center gap-1 py-2.5 text-[10px] font-semibold uppercase tracking-wider ${
                    isActive ? "text-primary" : "text-muted-foreground"
                  }`
                }
              >
                {item.primary ? (
                  <div className="mb-1 flex h-11 w-11 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-[0_8px_24px_rgba(197,75,60,0.4)]">
                    <item.icon size={22} weight="fill" />
                  </div>
                ) : (
                  <div className="relative">
                    <item.icon size={22} weight="duotone" />
                    {badge > 0 && (
                      <span
                        data-testid={`badge-${item.testid}`}
                        className="absolute -right-2 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary px-1 text-[9px] font-bold text-primary-foreground"
                      >
                        {badge > 9 ? "9+" : badge}
                      </span>
                    )}
                  </div>
                )}
                {!item.primary && <span>{item.label}</span>}
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
